import { IS_TENANT_MODE } from '../config/tenantMode';
import { useGetMasterBalanceQuery } from '../store/api/adminApi';

export type TenantPlan = 'numbers' | 'proxies' | 'both' | string;

export function planFlagsFromPlan(plan?: string | null) {
  const raw = String(plan || 'both').toLowerCase().trim();
  let normalized: TenantPlan = 'both';
  if (raw === 'numbers' || raw.includes('number')) normalized = 'numbers';
  else if (raw === 'proxies' || raw.includes('proxy')) normalized = 'proxies';
  else if (raw === 'both' || raw === 'all') normalized = 'both';
  else if (raw) normalized = raw;

  return {
    plan: normalized,
    hasNumbers: normalized === 'numbers' || normalized === 'both',
    hasProxies: normalized === 'proxies' || normalized === 'both',
  };
}

/** NUMBER service keys in system-config / cashback */
export const NUMBER_SERVICE_KEYS = new Set([
  'smspool',
  'pvadeals',
  'durianRcs',
  'textverified',
  'tellabot',
]);

/** Always available regardless of numbers/proxies plan (merchant warehouse) */
export const ALWAYS_ALLOWED_SERVICE_KEYS = new Set(['warehouse']);

/** PROXY service keys in system-config / cashback */
export const PROXY_SERVICE_KEYS = new Set([
  'proxycheap',
  'proxyline',
  'proxylte',
  'proxy5g',
  'proxygb',
]);

export function serviceAllowedByPlan(
  serviceKey: string,
  hasNumbers: boolean,
  hasProxies: boolean
): boolean {
  const key = String(serviceKey);
  if (ALWAYS_ALLOWED_SERVICE_KEYS.has(key)) return true;
  if (NUMBER_SERVICE_KEYS.has(key)) return hasNumbers;
  if (PROXY_SERVICE_KEYS.has(key)) return hasProxies;
  return true;
}

export function useTenantPlanFeatures() {
  const { data, isLoading, isError } = useGetMasterBalanceQuery(undefined, {
    skip: !IS_TENANT_MODE,
  });

  if (!IS_TENANT_MODE) {
    return {
      plan: 'both' as TenantPlan,
      hasNumbers: true,
      hasProxies: true,
      isLoading: false,
      isTenant: false,
    };
  }

  const fromApi = data?.data;
  // Avoid flashing the wrong plan features before master balance loads
  if (isLoading || !fromApi) {
    return {
      plan: 'both' as TenantPlan,
      hasNumbers: false,
      hasProxies: false,
      isLoading: true,
      isError,
      isTenant: true,
    };
  }

  const flags = planFlagsFromPlan(fromApi.plan);
  return {
    plan: flags.plan,
    hasNumbers: fromApi.hasNumbers ?? flags.hasNumbers,
    hasProxies: fromApi.hasProxies ?? flags.hasProxies,
    isLoading: false,
    isError,
    isTenant: true,
  };
}
